/**
 * Register Page - B2C user registration
 * Split layout: full-white left panel + marketing right
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authApi } from "../../services/authApi";
import { captureAppEvent } from "../../lib/posthogTracking";
import { useLang } from "../../shared/i18n";
import AuthSplitShell from "./AuthSplitShell";
import MicrosoftIcon from "./MicrosoftIcon";
import GoogleIcon from "./GoogleIcon";
import AuthLegalNotice from "./AuthLegalNotice";

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLang();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(t("passwordTooShort"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("passwordsNoMatch"));
      return;
    }
    setLoading(true);
    const result = await register(name, email, password);
    if (result.success) {
      captureAppEvent("user_signed_up", { method: "email" });
      const plan = new URLSearchParams(window.location.search).get("plan");
      navigate(
        `/verify?email=${encodeURIComponent(email)}${plan ? `&plan=${plan}` : ""}`,
      );
    } else {
      setError(result.message || t("registerFailed"));
    }
    setLoading(false);
  };

  const handleSsoLogin = async (provider: "microsoft" | "google") => {
    setSsoLoading(provider);
    setError("");
    const result =
      provider === "microsoft"
        ? await authApi.microsoftLogin()
        : await authApi.googleLogin();
    if (result.success && result.data?.authorization_url) {
      captureAppEvent("user_sso_redirected", { provider, page: "register" });
      window.location.href = result.data.authorization_url;
    } else {
      setError(result.message || t("ssoFailed"));
      setSsoLoading(null);
    }
  };

  const plan = new URLSearchParams(window.location.search).get("plan");
  const planNames: Record<string, string> = {
    starter: "Starter",
    pro: "Pro",
    payg: "Pay As You Go",
  };

  return (
    <AuthSplitShell>
      <h1>{t("registerTitle")}</h1>
      <p className="auth-panel__subtitle">{t("registerSubtitle")}</p>
      {plan && plan !== "free" ? (
        <div className="auth-plan-banner">
          <span className="auth-plan-banner__badge">
            {planNames[plan] || plan}
          </span>
          <span className="auth-plan-banner__text">
            {t("registerPlanAlmost")}
          </span>
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="auth-form">
        {error && <div className="auth-error">{error}</div>}
        <div className="form-group">
          <label htmlFor="name">{t("nameLabel")}</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("namePlaceholder")}
            required
            autoFocus
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">{t("emailLabel")}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">{t("passwordLabel")}</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("passwordMinLength")}
            required
            minLength={8}
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">{t("confirmPasswordLabel")}</label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("confirmPasswordPlaceholder")}
            required
          />
        </div>
        <button type="submit" className="auth-btn" disabled={loading}>
          {loading ? t("registerLoading") : t("registerButton")}
        </button>
      </form>
      <div className="auth-divider">
        <span>{t("orDivider")}</span>
      </div>
      <div className="auth-sso-buttons">
        <button
          type="button"
          className="auth-btn-sso"
          onClick={() => handleSsoLogin("google")}
          disabled={!!ssoLoading}
        >
          <GoogleIcon />{" "}
          {ssoLoading === "google" ? t("redirecting") : t("googleRegister")}
        </button>
        <button
          type="button"
          className="auth-btn-sso"
          onClick={() => handleSsoLogin("microsoft")}
          disabled={!!ssoLoading}
        >
          <MicrosoftIcon />{" "}
          {ssoLoading === "microsoft"
            ? t("redirecting")
            : t("microsoftRegister")}
        </button>
      </div>
      <AuthLegalNotice />
      <div className="auth-footer">
        <span>{t("hasAccount")} </span>
        <Link to="/login">{t("signIn")}</Link>
      </div>
    </AuthSplitShell>
  );
};

export default RegisterPage;
